import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { customToast } from "../../../components/notify";
import { FORM_STATE_OPTIONS } from "../../../constants";
import { AMAZE_WEBSITE_URL } from "../../../types/enum";
import { checkObjValueExists } from "../../../utils";
import { useGetVoicesByLanguageQuery } from "../../voice/services";
import { KIOSK_DEFAULT_VOICE, LOCALE_OPTIONS } from "../constants";
import { useGetAllCVoicesQuery, useUpdateCVoiceMutation } from "../services";
import { ICharacterFormInput, IVoice } from "../types";
import { VoiceSettingSchema } from "../validations";

const defaultValues = {
    voice_id: "",
    pitch: 1,
    speed: 1,
    language: "",
    gender: "male",
    voice_text: "",
};

const useCharacterVoiceSettings = () => {
    const { projectId, characterId } = useParams();
    const { t } = useTranslation();
    const methods = useForm<ICharacterFormInput>({
        defaultValues,
        resolver: yupResolver(VoiceSettingSchema()),
        mode: "all",
    });
    const { reset, setValue } = methods;

    const voiceQuery = {
        language: methods.watch("language"),
        gender: methods.watch("gender"),
    };
    const [voiceDataFromApiByName, setVoiceDataFromApiByName] = useState<IVoice | undefined>(undefined);
    // ================== API ==================
    const { data: voices, isLoading: vLoading } = useGetVoicesByLanguageQuery(voiceQuery, {
        skip: !voiceQuery.language || !voiceQuery.gender,
    });

    //取得當前的腳色所有語音資料
    const { data: characterVoiceData } = useGetAllCVoicesQuery({ character_id: characterId as string }); //list_voices
    const [updateCharacterVoice, { isLoading: uVLoading }] = useUpdateCVoiceMutation();

    // 預設先拿voiceData 的第一筆資料
    const firstCharacterVoiceData = characterVoiceData?.data?.[0];

    // 暫時排除高棉語, 因為如果設置 km-KH 後端會報錯誤 `/get_azure_audio_by_text` 會報錯誤
    const TEMP_LOCALE_OPTIONS = LOCALE_OPTIONS.filter((l) => l.value !== "km-KH");

    const processLanguageOptions = TEMP_LOCALE_OPTIONS.map((item) => {
        if (characterVoiceData?.data?.map((v) => v.language).includes(item.value)) {
            return item;
        }
        return { ...item, label: `${item.label} (Unset)` };
    });
    const defaultLangVoice = KIOSK_DEFAULT_VOICE.find(
        (d) => d.code === methods.watch("language") && d.gender.toLowerCase() === methods.watch("gender")
    );
    const voicesOptions = useMemo(() => {
        return (
            voices?.data?.map((item) => {
                const isDefault = item.azure_voice_short_name === defaultLangVoice?.voice;
                return {
                    label: isDefault ? `${item.voice_name} (${t("COMMON.SystemDefault")})` : item.voice_name,
                    value: item.id,
                };
            }) ?? []
        );
    }, [defaultLangVoice?.voice, t, voices?.data]);

    const audioPlayerProp = {
        language: methods.watch("language"),
        gender: methods.watch("gender"),
        pitch: methods.watch("pitch"),
        speed: methods.watch("speed"),
        short_name: voices?.data?.find((v) => v.id === methods.watch("voice_id"))?.azure_voice_short_name || "",
    };

    //>  ============== HANDLER ==============

    const findVoiceDataFromApi = useCallback(
        ({
            selectedId,
            selectedLanguage,
            selectedAzureVoiceShortName,
        }: {
            selectedId?: string;
            selectedLanguage?: string;
            selectedAzureVoiceShortName?: string;
        }) => {
            if (selectedId) {
                return characterVoiceData?.data?.find((v: IVoice) => v.source_voice_id === selectedId);
            }
            if (selectedLanguage) {
                return characterVoiceData?.data?.find((v: IVoice) => v.language === selectedLanguage);
            }
            if (selectedAzureVoiceShortName) {
                return voices?.data?.find((v: IVoice) => v.azure_voice_short_name === selectedAzureVoiceShortName);
            }
        },
        [characterVoiceData, voices]
    );

    const handleFormSubmit: SubmitHandler<ICharacterFormInput> = async (data) => {
        if (characterId && projectId) {
            const audioReq = {
                character_id: characterId || "",
                project_id: projectId,
                pitch: data.pitch,
                speed: data.speed,
                voice_id: data.voice_id,
                voice_text: data.voice_text || "",
            };
            try {
                // 保存表單提交前的選擇值供後續使用
                const savedVoiceId = data.voice_id;
                const savedVoiceName = voices?.data?.find((v) => v.id === savedVoiceId)?.voice_name;

                // 呼叫 API 更新聲音設置
                const result = await updateCharacterVoice(audioReq).unwrap();

                // RTK Query 會自動處理緩存更新，無需手動重新獲取
                // 使用 setTimeout 確保 UI 更新
                setTimeout(() => {
                    // 嘗試按名稱匹配找到對應的聲音（更可靠）
                    let matchedVoice = voices?.data?.find((voice) => voice.voice_name === savedVoiceName);

                    // 如果按名稱找不到，嘗試按 source_voice_id 匹配
                    if (!matchedVoice) {
                        matchedVoice = voices?.data?.find(
                            (voice) => voice.source_voice_id === result.data.source_voice_id
                        );
                    }

                    // 如果還是找不到，嘗試使用原始 id
                    if (!matchedVoice && savedVoiceId) {
                        matchedVoice = voices?.data?.find((voice) => voice.id === savedVoiceId);
                    }

                    // 如果找到對應的聲音，重設表單
                    if (matchedVoice) {
                        methods.resetField("voice_id");
                        methods.setValue("voice_id", matchedVoice.id, {
                            shouldValidate: true,
                            shouldDirty: false,
                            shouldTouch: false,
                        });
                    }

                    customToast.success("successfully updated");
                }, 300);
            } catch (err) {
                customToast.error("update failed");
            }
        }
    };

    const handleChangeLanguage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedLanguage = event.target.value;
        const voiceDataFromApi = findVoiceDataFromApi({ selectedLanguage });

        let temp = {
            ...defaultValues,
            language: event.target.value,
        };

        if (voiceDataFromApi) {
            temp = {
                ...temp,
                gender: voiceDataFromApi.gender,
                pitch: voiceDataFromApi.pitch,
                speed: voiceDataFromApi.speed,
                voice_text: voiceDataFromApi.voice_text,
            };

            // 先重設表單基本值，不包含 voice_id
            methods.reset(temp, { keepDirty: true, keepTouched: true });

            // 延遲設置 voice_id，給 voicesOptions 時間更新
            setTimeout(() => {
                // 嘗試按 source_voice_id 尋找匹配的聲音選項
                const matchingVoice = voices?.data?.find(
                    (v) =>
                        v.source_voice_id === voiceDataFromApi.source_voice_id ||
                        v.azure_voice_short_name === voiceDataFromApi.azure_voice_short_name
                );

                if (matchingVoice) {
                    methods.setValue("voice_id", matchingVoice.id, FORM_STATE_OPTIONS);
                } else {
                    // 如果找不到匹配的聲音，嘗試使用原始的 source_voice_id
                    methods.setValue("voice_id", voiceDataFromApi.source_voice_id, FORM_STATE_OPTIONS);
                }
            }, 300);
        } else {
            methods.reset(temp, { keepDirty: true, keepTouched: true });
        }
    };
    const handleChangeGender = (event: React.ChangeEvent<HTMLInputElement>) => {
        // 先設置性別，重設基本參數
        methods.setValue("gender", event.target.value, FORM_STATE_OPTIONS);
        methods.setValue("speed", 1, FORM_STATE_OPTIONS);
        methods.setValue("pitch", 1, FORM_STATE_OPTIONS);

        // 延遲設置聲音ID，確保聲音選項已經更新
        setTimeout(() => {
            if (voices?.data?.length) {
                // 如果有可用聲音，選擇第一個
                methods.setValue("voice_id", voices.data[0].id, FORM_STATE_OPTIONS);
            }
        }, 300);
    };

    const handleChangeVoiceId = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedVoiceId = event.target.value;
        const voiceDataFromApi = findVoiceDataFromApi({ selectedId: selectedVoiceId });

        // 選取原本儲存的腳色，會帶入語音資料
        // methods.setValue("voice_id", event.target.value, FORM_STATE_OPTIONS); //與下一行意義相同
        methods.setValue("voice_id", selectedVoiceId || "", FORM_STATE_OPTIONS);
        methods.setValue("speed", voiceDataFromApi?.speed || 1, FORM_STATE_OPTIONS);
        methods.setValue("pitch", voiceDataFromApi?.pitch || 1, FORM_STATE_OPTIONS);
    };

    const handleUpgrade = () => {
        window.open(AMAZE_WEBSITE_URL, "_blank");
    };

    //處理已存資料的id和apiGetAll的id不同的問題，選單為空的問題

    const defaultVoiceIdRef = useRef<string>("");

    // 1. API 拿到 voiceData 後，將首筆資料填入 form，但在儲存資料後不重新渲染
    useEffect(() => {
        if (firstCharacterVoiceData) {
            reset({
                pitch: firstCharacterVoiceData.pitch,
                speed: firstCharacterVoiceData.speed,
                gender: firstCharacterVoiceData.gender,
                language: firstCharacterVoiceData.language,
                voice_text: firstCharacterVoiceData.voice_text,
                voice_id: firstCharacterVoiceData.source_voice_id,
            });
        }
    }, [firstCharacterVoiceData, reset]);

    //2.找到相同azure_voice_short_name 新資料組
    useEffect(() => {
        if (!firstCharacterVoiceData || !voices?.data) return;

        if (!voiceDataFromApiByName) {
            const selectedAzureVoiceShortName = firstCharacterVoiceData?.azure_voice_short_name;
            const newApiData = findVoiceDataFromApi({ selectedAzureVoiceShortName });
            setVoiceDataFromApiByName(newApiData);
        }
    }, [findVoiceDataFromApi, firstCharacterVoiceData, voiceDataFromApiByName, voices?.data]);

    //3.使用新資料組的source_voice_id帶入form
    useEffect(() => {
        if (voiceDataFromApiByName) {
            const sameId = firstCharacterVoiceData?.source_voice_id === voiceDataFromApiByName?.id;
            const defaultVoiceId = sameId ? firstCharacterVoiceData?.source_voice_id : voiceDataFromApiByName?.id;
            defaultVoiceIdRef.current = defaultVoiceId as string;
            if (!sameId) {
                setValue("voice_id", defaultVoiceIdRef.current, {
                    shouldValidate: true,
                    shouldDirty: false,
                    shouldTouch: false,
                });
            }
        }
    }, [firstCharacterVoiceData, setValue, voiceDataFromApiByName, voices?.data]);

    return {
        pageLoading: vLoading,
        handleFormSubmit,
        methods,
        voicesOptions,
        handleChangeLanguage,
        handleChangeGender,
        handleChangeVoiceId,
        handleUpgrade,
        canTestVoice: checkObjValueExists(audioPlayerProp),
        audioPlayerProp,
        btnLoading: uVLoading,
        processLanguageOptions,
    };
};
export default useCharacterVoiceSettings;
